import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyOrderButton } from '@/components/CopyOrderButton';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { StockQuoteDetail } from '@/components/ui/StockQuote';
import { StrikeVisualization } from '@/components/ui/StrikeVisualization';
import { SpreadStrikes } from '@/components/ui/SpreadStrikes';
import { Spinner } from '@/components/ui/Spinner';
import { ChainJourney } from './ChainJourney';
import { ChainFlow } from './ChainFlow';
import { ChainPremiumFlow } from './ChainPremiumFlow';
import { JournalFeed } from '@/components/journal/JournalFeed';
import { SchwabLink } from '@/components/SchwabLink';
import { ShowFillsToggle } from '@/components/ui/ShowFillsToggle';
import { schwabResearchUrl, SCHWAB_OPTIONS_TICKET_URL } from '@/lib/schwabLinks';
import { useChain, useDeleteChain } from '@/hooks/queries/useChains';
import { useQuotes, useOptionLegsQuotes, useRollScenarios, useSpreadSuggestions } from '@/hooks/queries/useQuotes';
import { useMarketStatus } from '@/hooks/queries/useMarketStatus';
import { useOrdersBySymbol, useOrderAnalysis } from '@/hooks/queries/useOrders';
import { useShowFills } from '@/hooks/useShowFills';
import { useUrlParam } from '@/hooks/useUrlState';
import type { OpenOrder } from '@/lib/api/client';
import { formatDate, formatCurrency, formatCreditWithPerShare, formatStrategyType, parseOccSymbol, toLocalDate } from '@/lib/formatters';
import { consolidateLegs, type ConsolidatedLeg } from '@/lib/consolidateLegs';
import { cn } from '@/lib/utils';
import type { PositionWithLegs, PositionLeg, StrategyType } from '@steward/shared';
import { EventRiskLabel, CalendarCoverageNotice, firstEventRows, eventRowClasses, type EventRisk } from '@/components/EventRiskLabel';
import { ExplainDialog } from '@/components/learn/ExplainDialog';
import { buildLearnContext, finiteOrNull, perSharePremium, shortLegDeltas } from '@/learn';

function detectCurrentStrategy(legs: Array<{ optionType: string; action: string; quantityRemaining: number }>): StrategyType {
  // Only consider legs that are still open (or all legs for closed positions)
  const activLegs = legs.filter(l => l.quantityRemaining > 0);
  const check = activLegs.length > 0 ? activLegs : legs;
  const puts = check.filter(l => l.optionType === 'put');
  const calls = check.filter(l => l.optionType === 'call');
  if (check.length === 4 && puts.length === 2 && calls.length === 2) return 'iron_condor';
  if (check.length === 2) {
    if (puts.length === 2) return 'vertical_spread';
    if (calls.length === 2) return 'vertical_spread';
  }
  if (check.length === 1) {
    const leg = check[0];
    if (leg.action === 'STO' && leg.optionType === 'put') return 'cash_secured_put';
    if (leg.action === 'STO' && leg.optionType === 'call') return 'covered_call';
    return leg.optionType === 'put' ? 'single_put' : 'single_call';
  }
  return 'custom';
}

// All legs still open across every position in the chain. After a partial roll
// (e.g., rolled only the puts), an iron condor lives across two open positions
// — classification and the strike chart need to see them all.
function getAllActiveLegs<L extends { quantityRemaining: number }>(
  positions: Array<{ legs: L[] }>
): L[] {
  return positions.flatMap(p => p.legs.filter(l => l.quantityRemaining > 0));
}

/**
 * The option-quote query fields for one leg, or null when its OCC symbol will not parse.
 * The date is formatted from the parsed local Date, not re-serialised as UTC, because that is the
 * shape the quotes endpoint echoes back and the Greeks map is keyed on.
 */
function legQuoteFields(optionSymbol: string) {
  const parsed = parseOccSymbol(optionSymbol);
  if (!parsed) return null;
  const exp = parsed.expiration;
  const expirationDate = `${exp.getFullYear()}-${String(exp.getMonth() + 1).padStart(2, '0')}-${String(exp.getDate()).padStart(2, '0')}`;
  return { optionType: parsed.type, strikePrice: parsed.strike, expirationDate };
}

/**
 * The key the Greeks map is built under. Both the map and every lookup go through this one
 * function: a lookup that rebuilt the key by hand would miss silently, reporting "no deltas".
 */
function legQuoteKey(optionSymbol: string): string | null {
  const fields = legQuoteFields(optionSymbol);
  return fields ? `${fields.optionType}-${fields.strikePrice}-${fields.expirationDate}` : null;
}


interface ChainDetailProps {
  chainId: string;
}

export function ChainDetail({ chainId }: ChainDetailProps) {
  const navigate = useNavigate();
  const { data: chain, isLoading, error } = useChain(chainId);
  const deleteChain = useDeleteChain();

  // Determine current vs original strategy from position legs.
  // currentStrategy looks at ALL still-open legs across positions so a
  // per-side roll (puts now in a different position than calls) still
  // classifies as an iron condor.
  const positionsByRoll = chain?.positions.slice().sort((a, b) => a.rollNumber - b.rollNumber) ?? [];
  const firstPosition = positionsByRoll[0];
  const allActiveLegs = chain ? getAllActiveLegs(chain.positions) : [];
  const originalStrategy = firstPosition ? detectCurrentStrategy(firstPosition.legs) : null;
  const currentStrategy = allActiveLegs.length > 0
    ? detectCurrentStrategy(allActiveLegs)
    : (positionsByRoll[positionsByRoll.length - 1] ? detectCurrentStrategy(positionsByRoll[positionsByRoll.length - 1].legs) : null);
  const strategyChanged = originalStrategy && currentStrategy && originalStrategy !== currentStrategy;

  // Fetch quote for underlying symbol
  const symbols = chain ? [chain.underlyingSymbol] : [];
  const { data: quotes, isLoading: quotesLoading } = useQuotes(symbols);

  // Open positions: use the first as the "primary" for action buttons, but
  // merge active legs across every open position for chart + analysis.
  const openPositions = chain?.positions.filter((p) => p.status === 'open') ?? [];
  const openPosition = openPositions[0];
  const mergedActiveLegs = openPositions.flatMap(p => p.legs.filter(l => l.quantityRemaining > 0));
  const mergedOpenPosition = openPosition
    ? { ...openPosition, legs: mergedActiveLegs }
    : undefined;
  const optionLegsQuery = mergedActiveLegs
    .map((leg) => legQuoteFields(leg.optionSymbol))
    .filter((l): l is NonNullable<typeof l> => l !== null);

  const { data: optionLegsData } = useOptionLegsQuotes(
    chain?.underlyingSymbol ?? '',
    optionLegsQuery,
    chain?.status === 'open' && optionLegsQuery.length > 0
  );

  // Create a map of leg key -> Greeks for easy lookup
  const legGreeksMap = new Map<string, { delta: number | null; theta: number | null; iv: number | null }>();
  if (optionLegsData?.legs) {
    for (const legQuote of optionLegsData.legs) {
      const key = `${legQuote.optionType}-${legQuote.strikePrice}-${legQuote.expirationDate}`;
      legGreeksMap.set(key, {
        delta: legQuote.delta,
        theta: legQuote.theta,
        iv: legQuote.impliedVolatility,
      });
    }
  }

  // Stock position state — hooks must be before any early returns
  const [stockPosData, setStockPosData] = useState<Array<{
    id: string;
    underlyingSymbol: string;
    quantity: number;
    quantityRemaining: number;
    costBasisPerShare: number;
    status: string;
    realizedPnl: number;
    origin: string;
    tradeChainId: string | null;
    openedAt: string;
    closedAt: string | null;
  }>>([]);
  const [directPurchaseShares, setDirectPurchaseShares] = useState(0);
  const [explainOpen, setExplainOpen] = useState(false);

  // Fetch ALL stock positions for this underlying (not just this chain)
  // Filter by date overlap with chain's active period
  useEffect(() => {
    if (!chain) return;
    // Raw fetch, bypasses fetchApi/apiDateReviver by design (non-goal to
    // migrate) — stockPosData's openedAt/closedAt stay plain strings here
    // even though stockApi.* (which DOES go through fetchApi) now returns
    // real Dates for the same fields.
    fetch(`/api/stock-positions?symbol=${chain.underlyingSymbol}`, { credentials: 'include' })
      .then(r => r.json())
      .then((data: Array<typeof stockPosData[0] & { openedAt: string; closedAt: string | null }>) => {
        if (!Array.isArray(data)) { setStockPosData([]); return; }
        const chainStart = new Date(chain.openedAt);
        const chainEnd = chain.closedAt ? new Date(chain.closedAt) : new Date();
        // Filter to stock positions that overlapped with this chain's active period
        const overlapping = data.filter(sp => {
          const spStart = new Date(sp.openedAt);
          const spEnd = sp.closedAt ? new Date(sp.closedAt) : new Date();
          // Overlap: spStart <= chainEnd AND spEnd >= chainStart
          return spStart <= chainEnd && spEnd >= chainStart;
        });
        setStockPosData(overlapping);
        // Direct purchase shares (open, from other chains)
        const directShares = data
          .filter(sp =>
            sp.status === 'open' &&
            sp.tradeChainId !== chain.id &&
            sp.origin === 'direct_purchase'
          )
          .reduce((sum, sp) => sum + sp.quantityRemaining, 0);
        setDirectPurchaseShares(directShares);
      })
      .catch(() => { setStockPosData([]); setDirectPurchaseShares(0); });
  }, [chain?.id, chain?.underlyingSymbol]);

  // Fetch open orders for this symbol (only when chain is open)
  const { data: symbolOrders } = useOrdersBySymbol(
    chain?.underlyingSymbol ?? '',
    chain?.status === 'open'
  );

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this trade chain?')) return;
    await deleteChain.mutateAsync(chainId);
    navigate('/chains');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !chain) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-red-600">Failed to load trade chain</p>
          <Link to="/chains" className="text-brand-600 hover:text-brand-700 mt-2 inline-block">
            Back to chains
          </Link>
        </CardContent>
      </Card>
    );
  }

  const netPremium = chain.totalPremiumCollected - chain.totalPremiumPaid;
  const quote = quotes?.[chain.underlyingSymbol];

  const stockRealizedPnl = stockPosData.reduce((sum, sp) => sum + sp.realizedPnl, 0);
  const openStockShares = stockPosData.filter(sp => sp.status === 'open').reduce((sum, sp) => sum + sp.quantityRemaining, 0);
  const hasStockPositions = stockPosData.length > 0;

  // Find the closest SHORT strike for distance display (across all active legs)
  const shortLegs = mergedActiveLegs.filter(l => l.action === 'STO' || l.action === 'STC');
  let closestShortLeg = shortLegs[0];
  if (quote?.price && shortLegs.length > 0) {
    closestShortLeg = shortLegs.reduce((closest, leg) => {
      const closestDist = Math.abs(quote.price - closest.strikePrice);
      const legDist = Math.abs(quote.price - leg.strikePrice);
      return legDist < closestDist ? leg : closest;
    });
  }

  // --- Explain ("what am I looking at?") -------------------------------------
  // The dialog talks about the legs that are still live, so it is offered only when there are
  // any — buildLearnContext on an empty leg list reports held 0 and would explain nothing.
  // The position it chats to is the LATEST open one by roll number: on a chain that rolled one
  // side, the newest position is the one carrying the current strikes.
  const latestOpenPosition = openPositions
    .slice()
    .sort((a, b) => a.rollNumber - b.rollNumber)
    .at(-1);
  // ONE credit basis for both surfaces: `perSharePremium` (learn/context.ts) applied to the same
  // position the Risk view rows apply it to — this position's net premium in dollars over its own
  // open contracts. Summing every position's net premium and dividing by the CURRENT contract count
  // mixed a rolled chain's closed premium into a live position's per-share credit.
  const explainOpenLegs = latestOpenPosition?.legs.filter((l) => l.quantityRemaining > 0) ?? [];
  const explainCtx = buildLearnContext({
    symbol: chain.underlyingSymbol,
    legs: allActiveLegs,
    // A missing or unparseable quote is "no price", never NaN.
    price: finiteOrNull(quote?.price),
    netPremiumPerShare: latestOpenPosition
      ? perSharePremium(latestOpenPosition.netPremium, explainOpenLegs)
      : null,
    // The chain page has per-leg Greeks; the Risk view does not, and stays on delta's fallback.
    ...shortLegDeltas(
      allActiveLegs.map((leg) => ({
        optionType: leg.optionType,
        action: leg.action,
        strikePrice: leg.strikePrice,
        quantityRemaining: leg.quantityRemaining,
        greeksKey: legQuoteKey(leg.optionSymbol),
      })),
      legGreeksMap,
    ),
  });
  const explainChain = { rollCount: Math.max(0, positionsByRoll.length - 1) };

  // Sort positions chronologically (oldest first) for the nav
  const sortedPositions = [...chain.positions].sort(
    (a, b) => new Date(a.openedAt).getTime() - new Date(b.openedAt).getTime()
  );

  return (
    <div className="space-y-6">
      {/* Sticky header — breadcrumb, badges, actions, and position pills stay visible on scroll */}
      <div className="sticky top-[49px] z-20 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <Link
              to="/chains"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
              title="Back to all chains"
            >
              <svg className="w-4 h-4 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Chains
            </Link>
            <span className="text-gray-400">/</span>
            <Link
              to={`/symbols/${chain.underlyingSymbol}`}
              className="text-lg font-bold text-brand-600 hover:text-brand-700 transition-colors"
              title={`View all ${chain.underlyingSymbol} chains`}
            >
              {chain.underlyingSymbol}
            </Link>
            <SchwabLink
              href={schwabResearchUrl(chain.underlyingSymbol)}
              label={`${chain.underlyingSymbol} on Schwab`}
              ariaLabel={`Research ${chain.underlyingSymbol} on Schwab`}
            />
            {strategyChanged ? (
              <span className="flex items-center gap-1.5">
                <StrategyBadge strategy={currentStrategy ?? chain.strategyType} />
                <span className="text-xs text-gray-400" title={`Opened as ${formatStrategyType(originalStrategy!)}`}>
                  (was {formatStrategyType(originalStrategy!)})
                </span>
              </span>
            ) : (
              <StrategyBadge strategy={currentStrategy ?? chain.strategyType} />
            )}
            <StatusBadge status={chain.status} />
            <span className="text-xs text-gray-500 ml-1">
              Opened {formatDate(chain.openedAt)}
              {chain.closedAt && ` • Closed ${formatDate(chain.closedAt)}`}
            </span>
            {/* Sits with the strategy and status it explains, in the wrapping metadata group —
                the action group to the right does not wrap, and a sixth button there would push
                the row off a phone screen. */}
            {allActiveLegs.length > 0 && latestOpenPosition && (
              <button
                type="button"
                data-testid="explain"
                onClick={() => setExplainOpen(true)}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                Explain
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {openPosition && (
              <>
                <Link to={`/positions/${openPosition.id}/roll`}>
                  <Button variant="secondary">Roll Position</Button>
                </Link>
                <Link to={`/positions/${openPosition.id}`}>
                  <Button>Close Position</Button>
                </Link>
              </>
            )}
            <Link to={(() => {
              // Format in local time to match displayed dates
              const fmtLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              const from = fmtLocal(new Date(chain.openedAt));
              const to = chain.closedAt ? fmtLocal(new Date(chain.closedAt)) : fmtLocal(new Date());
              return `/transactions?symbol=${chain.underlyingSymbol}&from=${from}&to=${to}`;
            })()}>
              <Button variant="ghost">Transactions</Button>
            </Link>
            <SchwabLink
              href={SCHWAB_OPTIONS_TICKET_URL}
              label="Options ticket on Schwab"
              ariaLabel="Open Schwab options order ticket"
            />
            <Button variant="ghost" onClick={handleDelete} disabled={deleteChain.isPending}>
              Delete
            </Button>
          </div>
        </div>

        {/* Position Nav */}
        {sortedPositions.length > 1 && (
          <div className="flex items-center gap-1 text-sm overflow-x-auto pb-0.5">
            {sortedPositions.map((pos, i) => {
            const isActive = pos.status === 'open';
            const isLast = i === sortedPositions.length - 1;
            const label = i === 0
              ? 'Opened'
              : pos.status === 'open'
                ? 'Active'
                : pos.closeReason === 'rolled' ? 'Rolled' : 'Closed';
            const dateStr = (label === 'Closed' && pos.closedAt)
              ? formatDate(pos.closedAt)
              : formatDate(pos.openedAt);

            return (
              <div key={pos.id} className="flex items-center gap-1 flex-shrink-0">
                {i > 0 && (
                  <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
                <span className={cn(
                  'px-2 py-1 rounded text-xs whitespace-nowrap',
                  isActive
                    ? 'bg-green-100 text-green-700 font-semibold'
                    : isLast && pos.status === 'closed'
                      ? 'bg-gray-200 text-gray-600 font-medium'
                      : 'bg-gray-100 text-gray-500'
                )}>
                  {label} {dateStr}
                  {isActive && pos.legs?.[0] && (() => {
                    const exp = toLocalDate(pos.legs[0].expirationDate);
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    exp.setHours(0, 0, 0, 0);
                    const dte = Math.max(0, Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                    return <span className={cn('ml-1', dte <= 7 ? 'text-amber-600' : '')}>{dte} DTE</span>;
                  })()}
                </span>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Keyed by position id, as ExplainDialog requires, so its AI state resets per position. */}
      {latestOpenPosition && (
        <ExplainDialog
          key={latestOpenPosition.id}
          open={explainOpen}
          onClose={() => setExplainOpen(false)}
          positionId={latestOpenPosition.id}
          ctx={explainCtx}
          chain={explainChain}
        />
      )}

      {/* Active Position Analysis — only when there's an open position */}
      {mergedOpenPosition && quote?.price ? (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <StockQuoteDetail
              price={quote.price}
              change={quote.change ?? null}
              changePercent={quote.changePercent ?? null}
              strikePrice={closestShortLeg?.strikePrice}
              optionType={closestShortLeg?.optionType}
              isLoading={quotesLoading}
            />

            {/* Strike Visualization — all active legs across open positions */}
            {mergedActiveLegs.length > 1 && (
              <StrikeVisualization
                legs={mergedActiveLegs.map(l => ({
                  strikePrice: l.strikePrice,
                  optionType: l.optionType as 'call' | 'put',
                  action: l.action as 'BTO' | 'STO' | 'BTC' | 'STC',
                }))}
                currentPrice={quote.price}
              />
            )}

            {/* Position Analysis */}
            <SpreadAnalysis
              position={mergedOpenPosition}
              currentPrice={quote.price}
              optionLegsData={optionLegsData}
              underlyingSymbol={chain.underlyingSymbol}
              chainNetPremium={netPremium}
            />
          </CardContent>
        </Card>
      ) : chain.status !== 'closed' ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <p className="font-medium">No active position</p>
            <p className="text-sm mt-1">This chain&apos;s positions have been rolled or closed. Sync to update.</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Summary Cards */}
      <div className={cn(
        'grid gap-4',
        hasStockPositions
          ? 'grid-cols-2 lg:grid-cols-5'
          : 'grid-cols-2 lg:grid-cols-4'
      )}>
        <Card>
          <p className="text-sm text-gray-500">Net Premium</p>
          <p className={cn('text-lg font-semibold', netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
            {formatCurrency(netPremium)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Premium Collected</p>
          <p className="text-lg font-semibold text-green-600">
            {formatCurrency(chain.totalPremiumCollected)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Premium Paid</p>
          <p className="text-lg font-semibold text-gray-900">
            {formatCurrency(chain.totalPremiumPaid)}
          </p>
        </Card>
        {hasStockPositions && (
          <Card>
            <p className="text-sm text-gray-500">
              {openStockShares > 0 ? `Stock (${openStockShares} shares)` : 'Stock P&L'}
            </p>
            <p className={cn('text-lg font-semibold', stockRealizedPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
              {formatCurrency(stockRealizedPnl)}
            </p>
          </Card>
        )}
        <Card>
          <p className="text-sm text-gray-500">Realized P&L</p>
          <PnlDisplay value={chain.realizedPnl} size="lg" />
        </Card>
      </div>

      {/* Stock positions held during this chain's lifetime */}
      {(() => {
        const otherChainStock = stockPosData.filter(sp => sp.tradeChainId !== chain.id);
        const thisChainStock = stockPosData.filter(sp => sp.tradeChainId === chain.id);
        const allStockForDisplay = [...thisChainStock, ...otherChainStock];
        if (allStockForDisplay.length === 0 && directPurchaseShares === 0) return null;
        return (
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {chain.underlyingSymbol} Stock Held
            </div>
            <div className="space-y-1">
              {allStockForDisplay.map(sp => {
                const isThisChain = sp.tradeChainId === chain.id;
                const originLabel = sp.origin === 'assignment' ? 'Assigned' : sp.origin === 'exercise' ? 'Exercised' : 'Purchased';
                const dateRange = `${new Date(sp.openedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}${sp.closedAt ? ` – ${new Date(sp.closedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}` : ' – present'}`;
                return (
                  <div key={sp.id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className={cn('h-1.5 w-1.5 rounded-full', sp.status === 'open' ? 'bg-green-500' : 'bg-gray-400')} />
                      <span className="text-slate-600">
                        {sp.quantity} shares @ ${sp.costBasisPerShare.toFixed(2)}
                      </span>
                      <span className="text-slate-400">
                        {originLabel} {dateRange}
                      </span>
                      {!isThisChain && <span className="text-slate-400 italic">(other chain)</span>}
                    </div>
                    {sp.status === 'closed' && (
                      <span className={cn('font-medium', sp.realizedPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                        {formatCurrency(sp.realizedPnl, { showSign: true })}
                      </span>
                    )}
                    {sp.status === 'open' && (
                      <span className="text-green-600 font-medium">{sp.quantityRemaining} held</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Open Orders for this symbol */}
      {symbolOrders && symbolOrders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <svg className="w-4 h-4 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Open Orders
              <span className="text-xs font-normal text-gray-400">({symbolOrders.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {symbolOrders.map(order => (
                <ChainOpenOrder key={order.orderId} order={order} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Position Flow */}
      {chain.positions.length > 1 && (
        <Card>
          <CardContent className="pt-6">
            <ChainFlow positions={chain.positions} />
          </CardContent>
        </Card>
      )}

      {/* Chain Journey */}
      <Card>
        <CardContent className="pt-6">
          <ChainJourney
            positions={chain.positions}
          />
        </CardContent>
      </Card>

      <JournalFeed chainId={chain.id} symbol={chain.underlyingSymbol} title="Journal" />

      <ChainPremiumFlow positions={chain.positions} underlyingSymbol={chain.underlyingSymbol} chainId={chain.id} />

      {/* Notes */}
      {chain.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{chain.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {chain.tags && chain.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {chain.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export type AnalysisTab = 'close' | 'roll' | 'spread';

const ANALYSIS_TABS: readonly AnalysisTab[] = ['close', 'roll', 'spread'];

export interface SpreadAnalysisProps {
  position: PositionWithLegs;
  currentPrice: number;
  optionLegsData?: { legs: Array<{ optionType: string; strikePrice: number; expirationDate: string; mark?: number | null; bid?: number | null; ask?: number | null }> } | null;
  underlyingSymbol: string;
  chainNetPremium: number;
}

export function SpreadAnalysis({ position, currentPrice, optionLegsData, underlyingSymbol, chainNetPremium }: SpreadAnalysisProps) {
  // The tab is view state, so it rides in the URL: a link to a chain can be a link to its roll
  // ideas. The Explain dialog stays transient -- a shared link should not spring a modal open.
  const [tabParam, setActiveTab] = useUrlParam<AnalysisTab>('tab', 'close', ANALYSIS_TABS);
  const [showFills, setShowFills] = useShowFills();
  const legs = position.legs;

  const puts = legs.filter(l => l.optionType === 'put');
  const calls = legs.filter(l => l.optionType === 'call');

  const firstLeg = legs[0];
  if (!firstLeg) return null;

  const expDate = toLocalDate(firstLeg.expirationDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  expDate.setHours(0, 0, 0, 0);
  const dte = Math.max(0, Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

  const shortLegs = legs.filter(l => l.action === 'STO' || l.action === 'STC');
  const maxGain = chainNetPremium;

  let maxLoss = 0;
  let spreadWidth = 0;
  if (puts.length === 2 && calls.length === 2) {
    const putStrikes = puts.map(l => l.strikePrice).sort((a, b) => a - b);
    const callStrikes = calls.map(l => l.strikePrice).sort((a, b) => a - b);
    const putWidth = putStrikes[1] - putStrikes[0];
    const callWidth = callStrikes[1] - callStrikes[0];
    spreadWidth = Math.max(putWidth, callWidth);
    maxLoss = spreadWidth * (shortLegs[0]?.quantity || 1) * 100 - maxGain;
  } else if (puts.length === 2) {
    const strikes = puts.map(l => l.strikePrice).sort((a, b) => a - b);
    spreadWidth = strikes[1] - strikes[0];
    maxLoss = spreadWidth * (shortLegs[0]?.quantity || 1) * 100 - maxGain;
  } else if (calls.length === 2) {
    const strikes = calls.map(l => l.strikePrice).sort((a, b) => a - b);
    spreadWidth = strikes[1] - strikes[0];
    maxLoss = spreadWidth * (shortLegs[0]?.quantity || 1) * 100 - maxGain;
  }

  let costToClose: number | null = null;
  // The legs a quote came back without a price for — named in the close tab rather than blamed
  // on market hours, which is what it used to say whatever the reason.
  const unpriced: string[] = [];
  if (optionLegsData?.legs && optionLegsData.legs.length > 0) {
    let totalClose = 0;
    let allFound = true;
    for (const leg of legs) {
      const match = optionLegsData.legs.find(
        q => q.optionType === leg.optionType && Math.abs(q.strikePrice - leg.strikePrice) < 0.01
      );
      if (match?.mark != null) {
        const markValue = match.mark * leg.quantity * 100;
        if (leg.action === 'STO' || leg.action === 'STC') {
          totalClose -= markValue;
        } else {
          totalClose += markValue;
        }
      } else {
        allFound = false;
        unpriced.push(legLabel(leg));
      }
    }
    if (allFound) costToClose = totalClose;
  }

  // Assignment risk
  const shortPut = shortLegs.find(l => l.optionType === 'put');
  const shortCall = shortLegs.find(l => l.optionType === 'call');
  let assignmentRisk: 'none' | 'low' | 'moderate' | 'high' = 'none';
  let assignmentNote = '';
  if (shortPut && currentPrice <= shortPut.strikePrice) {
    assignmentRisk = dte <= 1 ? 'high' : dte <= 3 ? 'moderate' : 'low';
    assignmentNote = `Short ${shortPut.strikePrice}P is ITM by $${(shortPut.strikePrice - currentPrice).toFixed(2)}`;
  } else if (shortCall && currentPrice >= shortCall.strikePrice) {
    assignmentRisk = dte <= 1 ? 'high' : dte <= 3 ? 'moderate' : 'low';
    assignmentNote = `Short ${shortCall.strikePrice}C is ITM by $${(currentPrice - shortCall.strikePrice).toFixed(2)}`;
  } else if (shortPut) {
    const dist = currentPrice - shortPut.strikePrice;
    const pct = (dist / shortPut.strikePrice) * 100;
    if (pct < 2) {
      assignmentRisk = 'low';
      assignmentNote = `Short ${shortPut.strikePrice}P is $${dist.toFixed(2)} OTM (${pct.toFixed(1)}%)`;
    }
  }

  const expFormatted = expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // Strike distance analysis
  const shortPutStrike = shortLegs.find(l => l.optionType === 'put')?.strikePrice ?? null;
  const shortCallStrike = shortLegs.find(l => l.optionType === 'call')?.strikePrice ?? null;

  const putDistance = shortPutStrike !== null ? currentPrice - shortPutStrike : null;
  const putPercent = putDistance !== null && shortPutStrike ? (putDistance / shortPutStrike) * 100 : null;
  const callDistance = shortCallStrike !== null ? shortCallStrike - currentPrice : null;
  const callPercent = callDistance !== null && shortCallStrike ? (callDistance / shortCallStrike) * 100 : null;

  // Determine overall risk level from closest side
  const closestPercent = putPercent !== null && callPercent !== null
    ? Math.min(putPercent, callPercent)
    : putPercent ?? callPercent;
  const riskLevel: 'safe' | 'watch' | 'danger' = closestPercent !== null
    ? closestPercent <= 0 ? 'danger' : closestPercent < 3 ? 'danger' : closestPercent < 7 ? 'watch' : 'safe'
    : 'safe';

  const riskColors = { safe: 'bg-green-50 border-green-200', watch: 'bg-yellow-50 border-yellow-200', danger: 'bg-red-50 border-red-200' };
  const riskTextColors = { safe: 'text-green-700', watch: 'text-yellow-700', danger: 'text-red-700' };
  const riskLabels = { safe: 'Safe Zone', watch: 'Watch Closely', danger: 'At Risk' };

  // Determine if single-side spread (can suggest complementary)
  const isSingleSide = (puts.length >= 2 && calls.length === 0) || (calls.length >= 2 && puts.length === 0);
  const existingSide: 'put' | 'call' = puts.length >= 2 ? 'put' : 'call';

  const tabs = [
    { id: 'close' as const, label: 'Close' },
    { id: 'roll' as const, label: 'Roll' },
    ...(isSingleSide ? [{ id: 'spread' as const, label: `Add ${existingSide === 'put' ? 'Call' : 'Put'} Spread` }] : []),
  ];

  // Which tabs exist depends on the position, so a link carrying ?tab=spread can land on an iron
  // condor that has no spread tab. Fall back rather than render an empty panel.
  const activeTab: AnalysisTab = tabs.some((t) => t.id === tabParam) ? tabParam : 'close';

  return (
    <div className="space-y-3 text-sm">
      {/* Risk banner — only show for multi-leg when not safe */}
      {shortLegs.length > 0 && riskLevel !== 'safe' && (
        <div className={cn('rounded-lg border p-3 flex items-center justify-between', riskColors[riskLevel])}>
          <div className="flex items-center gap-2">
            <span className={cn('text-xs font-semibold uppercase', riskTextColors[riskLevel])}>{riskLabels[riskLevel]}</span>
            {assignmentRisk !== 'none' && (
              <span className={cn('text-xs', riskTextColors[riskLevel])}>{assignmentNote}</span>
            )}
          </div>
          {closestPercent !== null && closestPercent > 0 && (
            <span className={cn('text-xs font-medium', riskTextColors[riskLevel])}>
              {closestPercent.toFixed(1)}% from short strike
            </span>
          )}
        </div>
      )}

      {/* Stat boxes */}
      <div className={cn('grid gap-3', shortPutStrike !== null && shortCallStrike !== null ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-4')}>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1">Expiration</div>
          <div className="flex items-baseline gap-1.5">
            <span className={cn('text-xl font-bold', dte <= 3 ? 'text-red-600' : dte <= 7 ? 'text-amber-600' : 'text-gray-900')}>
              {dte}
            </span>
            <span className="text-gray-500">days</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">{expFormatted}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1">Max Gain / Loss</div>
          <div className="text-xl font-bold text-green-600">{formatCurrency(maxGain)}</div>
          {maxLoss > 0 && <div className="text-xs text-red-600 mt-0.5">Risk: -{formatCurrency(maxLoss)}</div>}
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1">Est. Cost to Close</div>
          {costToClose !== null ? (
            <>
              <div className={cn('text-xl font-bold', costToClose >= 0 ? 'text-green-600' : 'text-red-600')}>
                {formatCurrency(Math.abs(costToClose))}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                P&L: {formatCurrency(maxGain + costToClose, { showSign: true })}
              </div>
            </>
          ) : (
            <div className="text-xl font-bold text-gray-400">--</div>
          )}
        </div>
        {/* Strike distance boxes — one per side */}
        {shortPutStrike !== null && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Put ${shortPutStrike}</div>
            {putDistance !== null ? (
              <>
                <div className={cn('text-xl font-bold',
                  putPercent !== null && putPercent <= 0 ? 'text-red-600' : putPercent !== null && putPercent < 3 ? 'text-red-600' : putPercent !== null && putPercent < 7 ? 'text-yellow-600' : 'text-green-600'
                )}>
                  {putDistance <= 0 ? '-' : '+'}${Math.abs(putDistance).toFixed(2)}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{putPercent?.toFixed(1)}% {putDistance <= 0 ? 'ITM' : 'OTM'}</div>
              </>
            ) : <div className="text-xl font-bold text-gray-400">--</div>}
          </div>
        )}
        {shortCallStrike !== null && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Call ${shortCallStrike}</div>
            {callDistance !== null ? (
              <>
                <div className={cn('text-xl font-bold',
                  callPercent !== null && callPercent <= 0 ? 'text-red-600' : callPercent !== null && callPercent < 3 ? 'text-red-600' : callPercent !== null && callPercent < 7 ? 'text-yellow-600' : 'text-green-600'
                )}>
                  {callDistance <= 0 ? '-' : '+'}${Math.abs(callDistance).toFixed(2)}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{callPercent?.toFixed(1)}% {callDistance <= 0 ? 'ITM' : 'OTM'}</div>
              </>
            ) : <div className="text-xl font-bold text-gray-400">--</div>}
          </div>
        )}
      </div>

      {/* Action tabs */}
      <div className="bg-gray-50 rounded-lg overflow-hidden">
        <div className="flex border-b border-gray-200" role="tablist" data-testid="analysis-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              onClick={() => setActiveTab(tab.id)}
              aria-selected={activeTab === tab.id}
              className={cn(
                'flex-1 px-4 py-2.5 text-xs font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-white text-brand-600 border-b-2 border-brand-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4" data-testid="analysis-panel" data-tab={activeTab}>
          {activeTab === 'close' && (
            <CloseTab
              costToClose={costToClose}
              unpriced={unpriced}
              quotesLoaded={!!optionLegsData}
              maxGain={maxGain}
              legs={legs}
              optionLegsData={optionLegsData}
              showFills={showFills}
              onShowFillsChange={setShowFills}
            />
          )}
          {activeTab === 'roll' && (
            <RollTab underlyingSymbol={underlyingSymbol} position={position} netPremium={maxGain} />
          )}
          {activeTab === 'spread' && isSingleSide && (
            <SpreadSuggestionsTab
              underlyingSymbol={underlyingSymbol}
              existingSide={existingSide}
              spreadWidth={spreadWidth}
              quantity={shortLegs[0]?.quantity || 1}
              existingPremium={maxGain}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** "$410 put" — one leg as the close tab names it. Two fill lots of one contract name it once. */
function legLabel(leg: { optionType: string; strikePrice: number }): string {
  return `$${leg.strikePrice} ${leg.optionType}`;
}

/**
 * Why there is no cost to close, in words that are true. A quote that came back without some
 * legs names them; one that never came back says so. Neither blames the market clock: on
 * 2026-09-21 this read "check during market hours" at 11:27 on a trading day, because a leg sat
 * outside the strike window Schwab was asked for.
 */
export function closeUnavailableMessage(unpriced: string[], quotesLoaded: boolean): string {
  const named = [...new Set(unpriced)];
  if (quotesLoaded && named.length > 0) return `No quote for the ${named.join(', ')}`;
  return 'Option quotes unavailable right now';
}

function CloseTab({ costToClose, unpriced, quotesLoaded, maxGain, legs, optionLegsData, showFills, onShowFillsChange }: {
  costToClose: number | null;
  unpriced: string[];
  quotesLoaded: boolean;
  maxGain: number;
  legs: PositionLeg[];
  optionLegsData?: { legs: Array<{ optionType: string; strikePrice: number; mark?: number | null; bid?: number | null; ask?: number | null }> } | null;
  showFills: boolean;
  onShowFillsChange: (next: boolean) => void;
}) {
  if (costToClose === null) {
    return (
      <div data-testid="close-unavailable" className="text-center text-gray-500 py-4">
        {closeUnavailableMessage(unpriced, quotesLoaded)}
      </div>
    );
  }

  const contractCount = legs[0]?.quantityRemaining ?? legs[0]?.quantity ?? 1;
  const netPnl = maxGain + costToClose;
  const profitPercent = maxGain > 0 ? (netPnl / maxGain) * 100 : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-gray-600">Cost to close</span>
        <span className={cn('font-semibold', costToClose >= 0 ? 'text-green-600' : 'text-red-600')}>
          {formatCreditWithPerShare(Math.abs(costToClose), contractCount)} {costToClose >= 0 ? 'credit' : 'debit'}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-600">Net P&L if closed now</span>
        <span className={cn('font-bold text-lg', netPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
          {formatCreditWithPerShare(netPnl, contractCount, { showSign: true })}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-600">% of max profit</span>
        <span className={cn('font-medium', profitPercent >= 50 ? 'text-green-600' : profitPercent >= 0 ? 'text-gray-900' : 'text-red-600')}>
          {profitPercent.toFixed(0)}%
        </span>
      </div>

      {/* Per-leg breakdown with unrealized P&L */}
      <CloseLegDetail
        legs={legs}
        optionLegsData={optionLegsData}
        showFills={showFills}
        onShowFillsChange={onShowFillsChange}
      />
    </div>
  );
}

export interface CloseLegDetailProps {
  legs: PositionLeg[];
  optionLegsData?: { legs: Array<{ optionType: string; strikePrice: number; mark?: number | null; bid?: number | null; ask?: number | null }> } | null;
  showFills: boolean;
  onShowFillsChange: (next: boolean) => void;
}

/**
 * ChainDetail's leg table -- per-leg entry/mark/unrealized-P&L rows in the
 * Close tab. Consolidated by default (one row per contract at a weighted
 * average entry price); "Show fills" reveals the raw per-fill rows the
 * rank-1 pipeline actually stores. See task-20 brief.
 */
export function CloseLegDetail({ legs, optionLegsData, showFills, onShowFillsChange }: CloseLegDetailProps) {
  if (!optionLegsData?.legs || optionLegsData.legs.length === 0) return null;

  const rows: ConsolidatedLeg[] = showFills
    ? legs.map((leg) => ({ ...leg, fillCount: 1 }))
    : consolidateLegs(legs);

  return (
    <div className="border-t border-gray-200 pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-500">Per-leg detail</div>
        <ShowFillsToggle showFills={showFills} onChange={onShowFillsChange} />
      </div>
      <div className="space-y-1.5">
        {rows.map((leg, i) => {
          const match = optionLegsData.legs.find(
            q => q.optionType === leg.optionType && Math.abs(q.strikePrice - leg.strikePrice) < 0.01
          );
          const isShort = leg.action === 'STO' || leg.action === 'STC';
          const entry = leg.pricePerContract;
          const mark = match?.mark ?? null;
          // Unrealized P&L per leg:
          // Short: profit when mark < entry (we sold high, can buy back low)
          // Long: profit when mark > entry (we bought low, can sell high)
          const legPnl = mark != null
            ? isShort
              ? (entry - mark) * leg.quantityRemaining * 100
              : (mark - entry) * leg.quantityRemaining * 100
            : null;

          return (
            <div key={i} className="flex items-center justify-between py-1 text-xs">
              <span className="text-gray-600">
                {isShort ? 'Short' : 'Long'} ${leg.strikePrice} {leg.optionType.charAt(0).toUpperCase()} x{leg.quantityRemaining}
                {leg.fillCount > 1 && (
                  <span className="text-gray-400 ml-1">({leg.fillCount} fills)</span>
                )}
              </span>
              <div className="flex items-center gap-3 font-mono">
                <span className="text-gray-400" title="Entry price">
                  {entry > 0 ? `$${entry.toFixed(2)}` : '--'}
                </span>
                <span className="text-gray-400">→</span>
                <span title="Current mark">
                  {mark != null ? `$${mark.toFixed(2)}` : '--'}
                </span>
                {legPnl != null && (
                  <span className={cn('font-semibold min-w-[60px] text-right', legPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {formatCreditWithPerShare(legPnl, leg.quantityRemaining, { showSign: true })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Format a YYYY-MM-DD expiration day label for display.
 *
 * Deliberately parses at LOCAL midnight and formats with toLocaleDateString.
 * That is correct here and must not be "fixed" to UTC: an expiration is a day
 * label, not an instant, so it should read as the same calendar day in every
 * timezone. The opposite rule governs earnings and ex-dividend dates, which ARE
 * UTC-midnight instants and are formatted with getUTC* in EventRiskLabel.tsx --
 * reading those with local getters shows the previous day in the Americas.
 */
function formatExpDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export interface RollScenarioTableProps {
  scenarios: Array<{
    expiration: string;
    dte: number;
    legs: Array<{ optionType: string; strikePrice: number; action: string; mark: number | null; bid: number | null; ask: number | null; delta: number | null; theta: number | null; openInterest: number | null }>;
    rollCredit: number | null;
    netRollPnl: number | null;
    earningsInWindow: boolean;
    earningsDate: Date | null;
    earningsTiming: 'bmo' | 'amc' | null;
    exDivInWindow: boolean;
    exDivDate: Date | null;
    exDivAmount: number | null;
    exDivExceedsExtrinsic: boolean;
  }>;
  currentExp: string;
  netPremium: number;
  underlyingSymbol: string;
  calendarCoverage?: { hasData: boolean; lastConfirmedAt: Date | null };
  // Every dollar column formats through formatCreditWithPerShare(total,
  // contractCount, ...), and contractCount is derived from position.legs in
  // RollTab rather than from anything in `scenarios` -- so it has to cross the
  // boundary explicitly. It is a plain derived scalar, not RollTab state.
  //
  // REQUIRED, not optional-with-a-default-of-1. A forgotten contractCount would
  // divide a multi-contract roll credit by one and render a per-share figure
  // that is wrong by the contract count, silently and plausibly, on the screen
  // used to decide whether to roll. Required makes that a compile error; there
  // is exactly one caller and it already passes the value.
  contractCount: number;
}

/**
 * Presentational: no hooks, no query client, so it can be rendered in a test
 * with plain props. RollTab owns the fetching.
 */
export function RollScenarioTable({
  scenarios, currentExp, netPremium, underlyingSymbol, calendarCoverage, contractCount,
}: RollScenarioTableProps) {
  const eventBoundaries = firstEventRows(scenarios);


  // Get short leg delta from each scenario
  const getShortDelta = (scenario: RollScenarioTableProps['scenarios'][number]) => {
    const sl = scenario.legs.find(l => l.action === 'STO' || l.action === 'STC');
    return sl?.delta != null ? Math.abs(sl.delta) * 100 : null;
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <CalendarCoverageNotice symbol={underlyingSymbol} coverage={calendarCoverage} />
        <SchwabLink
          href={schwabResearchUrl(underlyingSymbol)}
          label={`${underlyingSymbol} on Schwab`}
          ariaLabel={`Research ${underlyingSymbol} on Schwab`}
        />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-500 border-b border-gray-200">
            <th className="text-left py-2 pr-1">Exp</th>
            <th className="text-right py-2 px-1">DTE</th>
            <th className="text-right py-2 px-1 hidden sm:table-cell">Delta</th>
            <th className="text-right py-2 px-1">
              <span className="hidden sm:inline">Roll Credit</span>
              <span className="sm:hidden">Credit / Net</span>
            </th>
            <th className="text-right py-2 px-1 hidden sm:table-cell">Net Roll</th>
            <th className="text-right py-2 pl-1">Total P&L</th>
          </tr>
        </thead>
        <tbody>
          {scenarios.map((scenario, i) => {
            const totalPnl = scenario.netRollPnl != null ? netPremium + scenario.netRollPnl : null;
            const shortDelta = getShortDelta(scenario);
            const isEarlier = scenario.expiration < currentExp;
            const isFirstLater = i > 0 && scenarios[i - 1].expiration < currentExp && scenario.expiration > currentExp;
            const isEarningsBoundary = eventBoundaries.earnings === i;
            const isExDivBoundary = eventBoundaries.exDiv === i;
            const isEventBoundary = isEarningsBoundary || isExDivBoundary;
            const risk: EventRisk = scenario;

            return (
              <tr key={scenario.expiration} className={cn(
                'border-b border-gray-100 last:border-b-0 hover:bg-gray-50',
                // Blue marks an earlier expiry; red marks money at risk. Red wins.
                isEarlier && !scenario.exDivExceedsExtrinsic && 'bg-blue-50/50',
                // The event boundary is the line the trader does not already
                // know; isFirstLater marks their own current expiration. Two
                // border-t-* colours would be resolved by CSS source order.
                isFirstLater && !isEventBoundary && 'border-t-2 border-t-gray-300',
                eventRowClasses({ risk, isEarningsBoundary, isExDivBoundary })
              )}>
                <td className="py-2 pr-1 font-medium">
                  <div>{formatExpDate(scenario.expiration)}</div>
                  {isEarlier && <div className="text-[10px] text-blue-500">earlier</div>}
                  <EventRiskLabel risk={risk} emphasis={isEventBoundary} />
                  {/* Delta on mobile, under date */}
                  {shortDelta != null && <div className="text-[10px] text-gray-400 sm:hidden">delta {shortDelta.toFixed(0)}</div>}
                </td>
                <td className="py-2 px-1 text-right text-gray-600">{scenario.dte}d</td>
                <td className="py-2 px-1 text-right text-gray-600 hidden sm:table-cell">
                  {shortDelta != null ? `${shortDelta.toFixed(0)}` : '--'}
                </td>
                {/* Desktop: separate columns */}
                <td className="py-2 px-1 text-right hidden sm:table-cell">
                  {scenario.rollCredit != null ? (
                    <span className={scenario.rollCredit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCreditWithPerShare(scenario.rollCredit, contractCount, { showSign: true })}
                    </span>
                  ) : <span className="text-gray-400">--</span>}
                </td>
                <td className="py-2 px-1 text-right hidden sm:table-cell">
                  {scenario.netRollPnl != null ? (
                    <span className={cn('font-medium', scenario.netRollPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                      {formatCreditWithPerShare(scenario.netRollPnl, contractCount, { showSign: true })}
                    </span>
                  ) : <span className="text-gray-400">--</span>}
                </td>
                {/* Mobile: stacked credit/net in one cell */}
                <td className="py-2 px-1 text-right sm:hidden">
                  <div>
                    {scenario.rollCredit != null ? (
                      <span className={cn('text-xs', scenario.rollCredit >= 0 ? 'text-green-600' : 'text-red-600')}>
                        {formatCreditWithPerShare(scenario.rollCredit, contractCount, { showSign: true })}
                      </span>
                    ) : <span className="text-gray-400 text-xs">--</span>}
                  </div>
                  <div>
                    {scenario.netRollPnl != null ? (
                      <span className={cn('text-[10px]', scenario.netRollPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                        net {formatCreditWithPerShare(scenario.netRollPnl, contractCount, { showSign: true })}
                      </span>
                    ) : <span className="text-gray-400 text-[10px]">--</span>}
                  </div>
                </td>
                <td className="py-2 pl-1 text-right">
                  {totalPnl != null ? (
                    <span className={cn('font-bold', totalPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                      {formatCreditWithPerShare(totalPnl, contractCount, { showSign: true })}
                    </span>
                  ) : <span className="text-gray-400">--</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function RollTab({ underlyingSymbol, position, netPremium }: {
  underlyingSymbol: string;
  position: PositionWithLegs;
  netPremium: number;
}) {
  const { data: marketStatus } = useMarketStatus();
  const rollLegs = position.legs
    .filter(l => l.quantityRemaining > 0)
    .map(leg => {
      const parsed = parseOccSymbol(leg.optionSymbol);
      const expDateStr = parsed
        ? `${parsed.expiration.getFullYear()}-${String(parsed.expiration.getMonth() + 1).padStart(2, '0')}-${String(parsed.expiration.getDate()).padStart(2, '0')}`
        : typeof leg.expirationDate === 'string'
          ? leg.expirationDate
          : new Date(leg.expirationDate).toISOString().split('T')[0];
      return {
        optionType: leg.optionType as 'call' | 'put',
        strikePrice: leg.strikePrice,
        expirationDate: expDateStr,
        action: leg.action as 'BTO' | 'STO' | 'BTC' | 'STC',
        quantity: leg.quantityRemaining,
      };
    });

  const contractCount = rollLegs[0]?.quantity ?? 1;

  // Strike adjustment state — keyed by "optionType-originalStrike"
  const [strikeAdjustments, setStrikeAdjustments] = useState<Record<string, number>>({});
  const [maintainWidth, setMaintainWidth] = useState(true);

  // Identify short and long legs for linked adjustments
  const shortLeg = rollLegs.find(l => l.action === 'STO' || l.action === 'STC');
  const longLeg = rollLegs.find(l => l.action === 'BTO' || l.action === 'BTC');
  const isSpread = shortLeg && longLeg && shortLeg.optionType === longLeg.optionType;

  // Build target strikes array for API
  const hasAdjustments = Object.keys(strikeAdjustments).length > 0;
  const targetStrikes = hasAdjustments
    ? rollLegs.map(leg => {
        const key = `${leg.optionType}-${leg.strikePrice}`;
        return {
          optionType: leg.optionType,
          originalStrikePrice: leg.strikePrice,
          strikePrice: strikeAdjustments[key] ?? leg.strikePrice,
        };
      }).filter(ts => ts.strikePrice !== ts.originalStrikePrice)
    : undefined;

  const { data, isLoading, error } = useRollScenarios(
    underlyingSymbol,
    rollLegs,
    rollLegs.length > 0,
    targetStrikes && targetStrikes.length > 0 ? targetStrikes : undefined
  );

  const adjustStrike = (leg: typeof rollLegs[0], delta: number) => {
    const key = `${leg.optionType}-${leg.strikePrice}`;
    const current = strikeAdjustments[key] ?? leg.strikePrice;
    const newStrike = Math.max(0.5, current + delta);

    const updates: Record<string, number> = { [key]: newStrike };

    // If maintain width is on and this is a spread, move the other leg too
    if (maintainWidth && isSpread) {
      const otherLeg = leg === shortLeg ? longLeg : shortLeg;
      if (otherLeg) {
        const otherKey = `${otherLeg.optionType}-${otherLeg.strikePrice}`;
        const otherCurrent = strikeAdjustments[otherKey] ?? otherLeg.strikePrice;
        updates[otherKey] = Math.max(0.5, otherCurrent + delta);
      }
    }

    setStrikeAdjustments(prev => ({ ...prev, ...updates }));
  };

  const resetStrikes = () => setStrikeAdjustments({});

  const getAdjustedStrike = (leg: typeof rollLegs[0]) =>
    strikeAdjustments[`${leg.optionType}-${leg.strikePrice}`] ?? leg.strikePrice;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
        <span className="ml-2 text-sm text-gray-500">Loading roll scenarios...</span>
      </div>
    );
  }

  if (error || !data || data.scenarios.length === 0) {
    const message = error && marketStatus && !marketStatus.isOpen
      ? `Market is closed — roll scenarios available during trading hours`
      : error
        ? 'Failed to load roll scenarios'
        : 'No available expirations found';
    return <div className="text-center text-sm text-gray-500 py-4">{message}</div>;
  }

  // Find where earlier/later split happens
  const currentExp = data.currentExpiration;

  // Format strike label for display
  const strikeLabel = rollLegs.map(l => {
    const adjusted = getAdjustedStrike(l);
    const suffix = l.optionType === 'put' ? 'P' : 'C';
    return `${adjusted}${suffix}`;
  }).join(' / ');

  return (
    <div>
      {/* Strike adjustment controls */}
      <div className="mb-3 pb-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-500">Target Strikes</span>
          <div className="flex items-center gap-3">
            {isSpread && (
              <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={maintainWidth}
                  onChange={(e) => setMaintainWidth(e.target.checked)}
                  className="h-3 w-3 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                Lock width
              </label>
            )}
            {hasAdjustments && (
              <button
                onClick={resetStrikes}
                className="text-[11px] text-brand-600 hover:text-brand-700"
              >
                Reset
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {rollLegs.map(leg => {
            const adjusted = getAdjustedStrike(leg);
            const isShort = leg.action === 'STO' || leg.action === 'STC';
            const label = isShort ? 'Short' : 'Long';
            const suffix = leg.optionType === 'put' ? 'P' : 'C';
            const changed = adjusted !== leg.strikePrice;

            return (
              <div key={`${leg.optionType}-${leg.strikePrice}`} className="flex items-center gap-1">
                <span className={cn('text-xs', isShort ? 'text-green-700' : 'text-red-600')}>
                  {label} {suffix}
                </span>
                <button
                  onClick={() => adjustStrike(leg, -5)}
                  className="w-6 h-6 flex items-center justify-center rounded bg-gray-50 hover:bg-gray-200 text-gray-400 text-[10px] font-medium"
                  title="-5"
                >
                  -5
                </button>
                <button
                  onClick={() => adjustStrike(leg, -1)}
                  className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium"
                >
                  -
                </button>
                <span className={cn(
                  'text-sm font-mono font-medium min-w-[3rem] text-center',
                  changed ? 'text-brand-600' : 'text-gray-900'
                )}>
                  ${adjusted}
                </span>
                <button
                  onClick={() => adjustStrike(leg, 1)}
                  className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium"
                >
                  +
                </button>
                <button
                  onClick={() => adjustStrike(leg, 5)}
                  className="w-6 h-6 flex items-center justify-center rounded bg-gray-50 hover:bg-gray-200 text-gray-400 text-[10px] font-medium"
                  title="+5"
                >
                  +5
                </button>
              </div>
            );
          })}
        </div>
        {hasAdjustments && (
          <div className="mt-1.5 text-[11px] text-brand-600">
            Rolling to {strikeLabel}
            {isSpread && ` ($${Math.abs(getAdjustedStrike(rollLegs[0]) - getAdjustedStrike(rollLegs[1]))} wide)`}
          </div>
        )}
      </div>

      <RollScenarioTable
        scenarios={data.scenarios}
        currentExp={currentExp}
        netPremium={netPremium}
        underlyingSymbol={underlyingSymbol}
        calendarCoverage={data.calendarCoverage}
        contractCount={contractCount}
      />
      <div className="mt-2 text-[11px] text-gray-400">
        Based on mark prices. Earlier expirations shown in blue; an amber rule marks where a target
        starts spanning a corporate event, and red flags a dividend worth more than the extrinsic
        left in the short call. Total P&L = original premium + net roll.
      </div>
    </div>
  );
}

const fillDifficultyConfig: Record<string, { label: string; color: string }> = {
  easy: { label: 'Likely to fill', color: 'text-green-700 bg-green-50 border-green-200' },
  moderate: { label: 'May need patience', color: 'text-yellow-700 bg-yellow-50 border-yellow-200' },
  hard: { label: 'Unlikely at this price', color: 'text-orange-700 bg-orange-50 border-orange-200' },
  unlikely: { label: 'Very unlikely', color: 'text-red-700 bg-red-50 border-red-200' },
};

function ChainOpenOrder({ order }: { order: OpenOrder }) {
  const hasOptions = order.legs.some(l => l.assetType === 'OPTION');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const { data: analysis, isLoading: analysisLoading } = useOrderAnalysis(
    showAnalysis ? order : null
  );

  return (
    <div className="border border-yellow-200 bg-yellow-50/50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 font-medium">
            {order.status}
          </span>
          <span className="text-xs text-gray-500">
            {order.orderType}
            {order.price != null && ` @ $${order.price.toFixed(2)}`}
          </span>
          {order.complexOrderStrategyType && order.complexOrderStrategyType !== 'NONE' && (
            <span className="text-xs text-gray-400">
              {order.complexOrderStrategyType.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <span className="text-[11px] text-gray-400">
          #{order.orderId}
        </span>
      </div>
      <div className="space-y-0.5">
        {order.legs.map((leg, i) => {
          const isSell = leg.instruction.includes('SELL');
          const shortInstr = leg.instruction
            .replace('BUY_TO_OPEN', 'BTO')
            .replace('BUY_TO_CLOSE', 'BTC')
            .replace('SELL_TO_OPEN', 'STO')
            .replace('SELL_TO_CLOSE', 'STC');
          return (
            <div key={i} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className={cn(
                  'text-xs font-medium w-8',
                  isSell ? 'text-red-600' : 'text-green-600'
                )}>
                  {shortInstr}
                </span>
                {hasOptions && leg.assetType === 'OPTION' ? (
                  <span className="text-gray-900 text-xs">
                    {leg.expirationDate && (() => {
                      const d = new Date(leg.expirationDate + 'T00:00:00');
                      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    })()}
                    {' '}
                    <span className="font-medium">${leg.strikePrice}</span>
                    {' '}
                    <span className="text-gray-500">{leg.putCall === 'PUT' ? 'P' : 'C'}</span>
                  </span>
                ) : (
                  <span className="text-gray-900 text-xs">{leg.symbol}</span>
                )}
              </div>
              <span className="text-gray-500 text-xs">x{leg.quantity}</span>
            </div>
          );
        })}
      </div>
      {/* Analysis toggle */}
      {hasOptions && (
        <div className="mt-2 pt-2 border-t border-yellow-200/50">
          <button
            onClick={() => setShowAnalysis(!showAnalysis)}
            className="text-[11px] text-brand-600 hover:text-brand-700 font-medium"
          >
            {showAnalysis ? 'Hide analysis' : 'Fill analysis'}
          </button>

          {showAnalysis && analysisLoading && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
              <Spinner size="sm" />
              Analyzing...
            </div>
          )}

          {showAnalysis && analysis && (
            <div className="mt-2 space-y-2">
              <div className={cn(
                'inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded border',
                fillDifficultyConfig[analysis.fillDifficulty]?.color || 'text-gray-500'
              )}>
                {fillDifficultyConfig[analysis.fillDifficulty]?.label || analysis.fillDifficulty}
              </div>

              <div className="flex gap-4 text-[11px]">
                <div>
                  <span className="text-gray-400">Natural </span>
                  <span className={cn('font-mono font-medium', analysis.naturalPrice >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {analysis.naturalPrice >= 0 ? '+' : ''}{(analysis.naturalPrice / (order.quantity * 100)).toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Mid </span>
                  <span className={cn('font-mono font-medium', analysis.midPrice >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {analysis.midPrice >= 0 ? '+' : ''}{(analysis.midPrice / (order.quantity * 100)).toFixed(2)}
                  </span>
                </div>
              </div>

              {analysis.suggestions.length > 0 && (
                <div className="space-y-1">
                  {analysis.suggestions.slice(0, 2).map((s, i) => (
                    <p key={i} className="text-[11px] text-gray-500 leading-relaxed">{s}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export interface SpreadSuggestionTableProps {
  suggestions: Array<{
    expiration: string;
    dte: number;
    shortStrike: number;
    longStrike: number;
    shortDelta: number;
    longDelta: number | null;
    credit: number;
    spreadWidth: number;
    shortTheta: number | null;
    shortIV: number | null;
    earningsInWindow: boolean;
    earningsDate: Date | null;
    earningsTiming: 'bmo' | 'amc' | null;
    exDivInWindow: boolean;
    exDivDate: Date | null;
    exDivAmount: number | null;
    exDivExceedsExtrinsic: boolean;
  }>;
  underlyingSymbol: string;
  existingPremium: number;
  calendarCoverage?: { hasData: boolean; lastConfirmedAt: Date | null };
  // Not in the Task 6 brief's prop list -- the moved table body references
  // both. `quantity` is a plain pass-through prop of SpreadSuggestionsTab (not
  // useState); `suggestSide` is a scalar derived from its `existingSide` prop.
  // Neither is component state, so neither needed lifting -- but both are
  // required, not defaulted, for the same reason contractCount is in
  // RollScenarioTableProps: quantity divides every per-share dollar figure and
  // suggestSide picks the option type on every leg, so a forgotten value would
  // silently render wrong numbers or wrong contracts.
  quantity: number;
  suggestSide: 'put' | 'call';
}

/** Presentational: no hooks, so it renders in a test with plain props. */
export function SpreadSuggestionTable({
  suggestions, underlyingSymbol, existingPremium, calendarCoverage, quantity, suggestSide,
}: SpreadSuggestionTableProps) {
  const eventBoundaries = firstEventRows(suggestions);


  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <CalendarCoverageNotice symbol={underlyingSymbol} coverage={calendarCoverage} />
        <SchwabLink
          href={schwabResearchUrl(underlyingSymbol)}
          label={`${underlyingSymbol} on Schwab`}
          ariaLabel={`Research ${underlyingSymbol} on Schwab`}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-200">
              <th className="text-left py-2 pr-2">Exp</th>
              <th className="text-right py-2 px-2">DTE</th>
              <th className="text-left py-2 px-2">Strikes</th>
              <th className="text-right py-2 px-2">Credit</th>
              <th className="text-right py-2 pl-2">Combined</th>
              <th className="text-right py-2 pl-2">Copy</th>
            </tr>
          </thead>
          <tbody>
            {suggestions.map((s, i) => {
              const combinedPremium = existingPremium + s.credit;
              const isEarningsBoundary = eventBoundaries.earnings === i;
              const isExDivBoundary = eventBoundaries.exDiv === i;
              const isEventBoundary = isEarningsBoundary || isExDivBoundary;
              return (
                <tr key={s.expiration} className={cn(
                  'border-b border-gray-100 last:border-b-0 hover:bg-gray-50',
                  eventRowClasses({ risk: s, isEarningsBoundary, isExDivBoundary })
                )}>
                  <td className="py-2 pr-2 font-medium">
                    <div>{formatExpDate(s.expiration)}</div>
                    <EventRiskLabel risk={s} emphasis={isEventBoundary} />
                  </td>
                  <td className="py-2 px-2 text-right text-gray-600">{s.dte}d</td>
                  <td className="py-2 px-2">
                    <SpreadStrikes
                      shortStrike={s.shortStrike}
                      longStrike={s.longStrike}
                      optionType={suggestSide === 'call' ? 'call' : 'put'}
                      shortDelta={s.shortDelta}
                    />
                  </td>
                  <td className="py-2 px-2 text-right">
                    <span className={s.credit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCreditWithPerShare(s.credit, quantity, { showSign: true })}
                    </span>
                  </td>
                  <td className="py-2 pl-2 text-right">
                    <span className={cn('font-bold', combinedPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
                      {formatCreditWithPerShare(combinedPremium, quantity, { showSign: true })}
                    </span>
                  </td>
                  <td className="py-2 pl-2 text-right">
                    <CopyOrderButton
                      order={{
                        symbol: underlyingSymbol,
                        strategy: suggestSide === 'call' ? 'Vertical Call Spread' : 'Vertical Put Spread',
                        legs: [
                          { action: 'STO', quantity, symbol: underlyingSymbol, expiration: s.expiration, strike: s.shortStrike, optionType: suggestSide === 'call' ? 'call' : 'put' },
                          { action: 'BTO', quantity, symbol: underlyingSymbol, expiration: s.expiration, strike: s.longStrike, optionType: suggestSide === 'call' ? 'call' : 'put' },
                        ],
                        netCredit: (s.credit / quantity) / 100,
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SpreadSuggestionsTab({ underlyingSymbol, existingSide, spreadWidth, quantity, existingPremium }: {
  underlyingSymbol: string;
  existingSide: 'put' | 'call';
  spreadWidth: number;
  quantity: number;
  existingPremium: number;
}) {
  const { data: marketStatus } = useMarketStatus();
  const suggestSide = existingSide === 'put' ? 'call' : 'put';
  const { data, isLoading, error } = useSpreadSuggestions({
    underlyingSymbol,
    existingSide,
    spreadWidth: spreadWidth || 2,
    quantity,
    targetDeltaMin: 10,
    targetDeltaMax: 15,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
        <span className="ml-2 text-sm text-gray-500">Finding {suggestSide} spreads...</span>
      </div>
    );
  }

  if (error || !data || data.suggestions.length === 0) {
    const message = error && marketStatus && !marketStatus.isOpen
      ? `Market is closed — spread suggestions available during trading hours`
      : error
        ? 'Failed to load suggestions'
        : `No ${suggestSide} spreads found at ${data?.deltaRange.min}-${data?.deltaRange.max} delta`;
    return <div className="text-center text-sm text-gray-500 py-4">{message}</div>;
  }

  return (
    <div>
      <div className="text-xs text-gray-500 mb-3">
        {suggestSide === 'call' ? 'Call' : 'Put'} spreads at {data.deltaRange.min}-{data.deltaRange.max} delta
        {data.underlyingPrice > 0 && ` (${underlyingSymbol} @ $${data.underlyingPrice.toFixed(2)})`}
      </div>
      <SpreadSuggestionTable
        suggestions={data.suggestions}
        underlyingSymbol={underlyingSymbol}
        existingPremium={existingPremium}
        calendarCoverage={data.calendarCoverage}
        quantity={quantity}
        suggestSide={suggestSide}
      />
      <div className="mt-2 text-[11px] text-gray-400">
        Adding a {suggestSide} spread creates an iron condor. Combined = existing premium + new credit.
      </div>
    </div>
  );
}
